import type { optionType } from './dictionary.ts'
import { TrackingEvent } from './Exponea.ts'
import type { Timestamp } from './firebase.ts'
import type { UserId } from './ids.ts'
import type { Mentoring } from './Mentoring.ts'
import type { ParticipationRequest } from './ParticipationRequest.ts'

export type FeedbackId = string

export enum FeedbackAction {
    JOB_CREATE = 'job-create',
    JOB_APPLICATION = 'job-application',
    JOB_UNPUBLISH = 'job-unpublish',
    PROJECT_CREATE = 'project-create',
    VOLUNTEER_REQUEST_REJECT = 'volunteer-request-reject',
    PROJECT_REQUEST_REJECT = 'project-request-reject',
    PROJECT_UNPUBLISH = 'project-unpublish',
    PROJECT_COOPERATION_VOLUNTEER = 'project-cooperation-volunteer',
    PROJECT_COOPERATION_ORG = 'project-cooperation-org',
    COOPERATION_FINISH_LOW_RATING = 'cooperation-finish-low-rating',
    COOPERATION_FINISH_HIGH_RATING = 'cooperation-finish-high-rating',
    MENTORING_REQUEST_REJECT = 'mentoring-request-reject',
    MENTORING_COOPERATION_MENTOR = 'mentoring-cooperation-mentor',
    MENTORING_COOPERATION_MENTEE = 'mentoring-cooperation-mentee',
    MENTORING_FINISH_LOW_RATING = 'mentoring-finish-low-rating',
    MENTORING_FINISH_HIGH_RATING = 'mentoring-finish-high-rating',
    MYPROJECTS_COMPLETE = 'myprojects-complete'
}

export const trackingActionToFeedbackActionMap = {
    [TrackingEvent.PROJECT_CREATED]: FeedbackAction.PROJECT_CREATE,
    [TrackingEvent.PROJECT_COOPERATION_FEEDBACK_ORG]: FeedbackAction.PROJECT_COOPERATION_ORG,
    [TrackingEvent.PROJECT_COOPERATION_FEEDBACK_VOLUNTEER]: FeedbackAction.PROJECT_COOPERATION_VOLUNTEER,
    [TrackingEvent.MENTORING_COOPERATION_FEEDBACK_MENTOR]: FeedbackAction.MENTORING_COOPERATION_MENTOR,
    [TrackingEvent.MENTORING_COOPERATION_FEEDBACK_MENTEE]: FeedbackAction.MENTORING_COOPERATION_MENTEE
}

export interface FeedbackData {
    id?: string
    action: FeedbackAction
    /** @deprecated legacy 1-5 rating, read-only for historical records */
    stars?: FeedbackStars
    nps?: number
    request: ParticipationRequest | Mentoring | any
    requestId?: string
    userId: string
}

export interface FeedbackQuestionData {
    question: string
    options: optionType[]
    withText?: boolean
    textOnly?: boolean
    placeholder?: string
}

export interface FeedbackAnswerData {
    question: string
    /** @deprecated legacy 1-5 rating, read-only for historical records */
    stars?: FeedbackStars
    nps?: number
    option?: string
    message?: string
}

export enum FeedbackOption {
    OTHER_REASON = 'iny-dovod'
}

export const volunteerRequestRejectFeedbackOptions: optionType[] = [
    {
        id: 'nemam-cas',
        value: 'nemam-cas',
        label: 'Momentálne nemám čas'
    },
    {
        id: 'nevhodny',
        value: 'nevhodny',
        label: 'Projekt nebol vhodný pre mňa'
    },
    {
        id: 'nereagovali',
        value: 'nereagovali',
        label: 'Organizácia nereagovala'
    },
    {
        id: FeedbackOption.OTHER_REASON,
        value: FeedbackOption.OTHER_REASON,
        label: 'Iný dôvod'
    }
]

export const projectRequestRejectFeedbackOptions: optionType[] = [
    {
        id: 'irelevantny',
        value: 'irelevantny',
        label: 'Projekt už nie je relevantý'
    },
    {
        id: 'nevhodny',
        value: 'nevhodny',
        label: 'Dobrovoľník/čka nebol/a vhodný/á na ponuku'
    },
    {
        id: 'nereagoval',
        value: 'nereagoval',
        label: 'Dobrovoľník/čka nereagoval/a'
    },
    {
        id: FeedbackOption.OTHER_REASON,
        value: FeedbackOption.OTHER_REASON,
        label: 'Iný dôvod'
    }
]

export const mentoringRequestRejectFeedbackOptions: optionType[] = [
    {
        id: 'nevhodny',
        value: 'nevhodny',
        label: 'Mentee nebol vhodný pre mňa'
    },
    {
        id: 'nevedel',
        value: 'nevedel',
        label: 'Nevedel/a som čo spraviť'
    },
    {
        id: 'nereagoval',
        value: 'nereagoval',
        label: 'Mentee nereagoval'
    },
    {
        id: FeedbackOption.OTHER_REASON,
        value: FeedbackOption.OTHER_REASON,
        label: 'Iný dôvod'
    }
]

export const mentoringFinishFeedbackOptions: optionType[] = [
    {
        id: 'druha-strana',
        value: 'druha-strana',
        label: 'Druhá strana prestala komunikovať'
    },
    {
        id: 'zly-fit',
        value: 'zly-fit',
        label: 'Neboli sme dobrý fit (expertízou / požiadavkami na projekt alebo osobnostne)'
    },
    {
        id: 'moja-strana',
        value: 'moja-strana',
        label: 'Zlyhalo to z mojej strany (kvôli mojej časovej kapacite)'
    },
    {
        id: 'zmena-poziadaviek',
        value: 'zmena-poziadaviek',
        label: 'Zmenili sa požiadavky na spoluprácu'
    },
    {
        id: FeedbackOption.OTHER_REASON,
        value: FeedbackOption.OTHER_REASON,
        label: 'Iný dôvod'
    }
]

export const cooperationFinishFeedbackOptions: optionType[] = [
    {
        id: 'druha-strana',
        value: 'druha-strana',
        label: 'Druhá strana prestala komunikovať'
    },
    {
        id: 'zly-fit',
        value: 'zly-fit',
        label: 'Neboli sme dobrý fit (expertízou / požiadavkami na projekt alebo osobnostne)'
    },
    {
        id: 'moja-strana',
        value: 'moja-strana',
        label: 'Zlyhalo to z mojej strany (kvôli mojej časovej kapacite)'
    },
    {
        id: 'zmena-poziadaviek',
        value: 'zmena-poziadaviek',
        label: 'Zmenili sa požiadavky na spoluprácu'
    },
    {
        id: FeedbackOption.OTHER_REASON,
        value: FeedbackOption.OTHER_REASON,
        label: 'Iný dôvod'
    }
]

/**
 * Legacy 1-5 rating. Kept so historical `feedbacks` documents and the old email
 * rating codes stay readable - new feedback is collected as NPS (see below).
 */
export enum FeedbackStars {
    ONE = '1',
    TWO = '2',
    THREE = '3',
    FOUR = '4',
    FIVE = '5'
}

/**
 * Official NPS banding, matching how GROWNi reports NPS outside the product:
 * detractors 0-6, passives 7-8, promoters 9-10. Anything <= 6 counts as an
 * unsatisfied customer.
 */
export enum NpsBand {
    DETRACTOR = 'detractor',
    PASSIVE = 'passive',
    PROMOTER = 'promoter'
}

export const NPS_MIN_SCORE = 0
export const NPS_MAX_SCORE = 10

/** Highest score still counted as an unsatisfied customer. */
export const NPS_DETRACTOR_MAX_SCORE = 6
/** Highest passive score - above this the answer is a promoter. */
export const NPS_PASSIVE_MAX_SCORE = 8

export const npsScores: number[] = Array.from({ length: NPS_MAX_SCORE - NPS_MIN_SCORE + 1 }, (_, i) => NPS_MIN_SCORE + i)

export const feedbackStarsCodes = {
    [FeedbackStars.ONE]: 'FS71',
    [FeedbackStars.TWO]: 'FS72',
    [FeedbackStars.THREE]: 'FS73',
    [FeedbackStars.FOUR]: 'FS74',
    [FeedbackStars.FIVE]: 'FS75'
}

export interface FeedbackFormData {
    /** @deprecated legacy 1-5 rating, read-only for historical records */
    stars?: FeedbackStars
    nps?: number
    npsBand?: NpsBand
    option?: FeedbackOption | string
    message?: string
    answers?: FeedbackAnswerData[]
}

export interface Feedback extends FeedbackFormData {
    id: FeedbackId

    userId?: UserId
    email?: string
    message?: string
    action: FeedbackAction
    createdAt: Timestamp
    requestId?: string
}

export const feedbackQuestions = {
    [FeedbackAction.JOB_CREATE]: [
        {
            question: 'jobCreate.question',
            placeholder: 'jobCreate.placeholder'
        }
    ],
    [FeedbackAction.JOB_APPLICATION]: [
        {
            question: 'jobApplication.question'
        }
    ],
    [FeedbackAction.JOB_UNPUBLISH]: [
        {
            question: 'jobUnpublish.question'
        }
    ],
    [FeedbackAction.PROJECT_CREATE]: [
        {
            question: 'projectCreate.question'
        }
    ],
    [FeedbackAction.VOLUNTEER_REQUEST_REJECT]: [
        {
            question: 'participationRequestReject.question',
            options: volunteerRequestRejectFeedbackOptions
        }
    ],
    [FeedbackAction.PROJECT_REQUEST_REJECT]: [
        {
            question: 'participationRequestReject.question',
            options: projectRequestRejectFeedbackOptions
        }
    ],
    [FeedbackAction.PROJECT_UNPUBLISH]: [
        {
            question: 'projectUnpublish.question'
        }
    ],
    // NPS first, then the free-text follow-up picked by the band - same shape as the
    // Bloomreach NPS mails.
    [FeedbackAction.PROJECT_COOPERATION_VOLUNTEER]: [
        {
            question: 'projectCooperationVolunteer.question2',
            placeholder:
                'projectCooperationVolunteer.placeholder2-detractor|projectCooperationVolunteer.placeholder2-promoter'
        }
    ],
    [FeedbackAction.PROJECT_COOPERATION_ORG]: [
        {
            question: 'projectCooperationOrg.npsQuestion'
        }
    ],
    [FeedbackAction.MENTORING_REQUEST_REJECT]: [
        {
            question: 'mentoringRequestReject.question',
            options: mentoringRequestRejectFeedbackOptions
        }
    ],
    [FeedbackAction.MENTORING_COOPERATION_MENTOR]: [
        {
            question: 'mentoringCooperationMentor.question2',
            placeholder:
                'mentoringCooperationMentor.placeholder2-detractor|mentoringCooperationMentor.placeholder2-promoter'
        }
    ],
    [FeedbackAction.MENTORING_COOPERATION_MENTEE]: [
        {
            question: 'mentoringCooperationMentee.question2',
            placeholder:
                'mentoringCooperationMentee.placeholder2-detractor|mentoringCooperationMentee.placeholder2-promoter'
        }
    ],
    [FeedbackAction.MENTORING_FINISH_LOW_RATING]: [
        {
            question: 'mentoringFinish.question1.low-rating',
            options: mentoringFinishFeedbackOptions,
            placeholder: 'mentoringFinish.placeholder1.low-rating'
        }
    ],
    [FeedbackAction.MENTORING_FINISH_HIGH_RATING]: [
        {
            question: 'mentoringFinish.question1.high-rating',
            textOnly: true,
            placeholder: 'mentoringFinish.placeholder1.high-rating'
        }
    ],
    [FeedbackAction.COOPERATION_FINISH_LOW_RATING]: [
        {
            question: 'cooperationFinish.question1.low-rating',
            options: cooperationFinishFeedbackOptions,
            placeholder: 'cooperationFinish.placeholder1.low-rating'
        }
    ],
    [FeedbackAction.COOPERATION_FINISH_HIGH_RATING]: [
        {
            question: 'cooperationFinish.question1.high-rating',
            textOnly: true,
            placeholder: 'cooperationFinish.placeholder1.high-rating'
        }
    ],
    [FeedbackAction.MYPROJECTS_COMPLETE]: [
        {
            question: 'myProjectsComplete.question1'
        }
    ]
}
